import React, { useState } from 'react';
import { Search, Filter, Eye, Trash2, Send } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import QuoteFormModal from '../../components/admin/QuoteFormModal';
import { useAuth } from '../../lib/auth';
import { useQuoteRequestsStore, useQuotesStore } from '../../lib/db';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  processing: 'bg-blue-100 text-blue-800',
  sent: 'bg-green-100 text-green-800',
  accepted: 'bg-purple-100 text-purple-800',
  rejected: 'bg-red-100 text-red-800',
};

const statusLabels = {
  pending: 'En attente',
  processing: 'En cours',
  sent: 'Envoyé',
  accepted: 'Accepté',
  rejected: 'Refusé',
};

export default function QuotesPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState<string | null>(null);
  const { user } = useAuth();
  const { getRequestsByAgency, updateRequest } = useQuoteRequestsStore();
  const { getQuotesByAgency, getQuoteByRequestId } = useQuotesStore();

  const requests = user?.agencyId ? getRequestsByAgency(user.agencyId) : [];
  const quotes = user?.agencyId ? getQuotesByAgency(user.agencyId) : [];

  // Filtrer les demandes selon les critères
  const filteredRequests = requests.filter(request => {
    const matchesSearch = 
      `${request.firstName} ${request.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.destination.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || request.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  const handleCreateQuote = (requestId: string) => {
    updateRequest(requestId, { status: 'processing' });
    setSelectedRequest(requestId);
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'dd MMMM yyyy', { locale: fr });
  };

  const formatBudget = (budget: string) => {
    switch (budget) {
      case 'economy': return 'Moins de 50 000 DA';
      case 'standard': return '50 000 - 100 000 DA';
      case 'premium': return '100 000 - 200 000 DA';
      case 'luxury': return 'Plus de 200 000 DA';
      default: return budget;
    }
  };

  const formatDuration = (duration: string) => {
    switch (duration) {
      case 'weekend': return 'Week-end (2-3 jours)';
      case 'week': return 'Une semaine';
      case 'twoweeks': return 'Deux semaines';
      case 'more': return 'Plus de deux semaines';
      default: return duration;
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">Demandes de devis</h1>
        </div>

        {/* Filtres et recherche */}
        <div className="flex gap-4 bg-white p-4 rounded-lg shadow">
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder="Rechercher une demande..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md"
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          </div>

          <div className="relative">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md appearance-none"
            >
              <option value="all">Tous les statuts</option>
              {Object.entries(statusLabels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          </div>
        </div>

        {/* Liste des demandes */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {filteredRequests.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              Aucune demande de devis trouvée
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Destination
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date de départ
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Budget
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredRequests.map((request) => {
                    const quote = getQuoteByRequestId(request.id);
                    return (
                      <tr key={request.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {request.firstName} {request.lastName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {request.email}
                          </div>
                          <div className="text-sm text-gray-500">
                            {request.phone}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {request.destination}
                          </div>
                          <div className="text-sm text-gray-500">
                            {formatDuration(request.duration)}
                          </div>
                          <div className="text-sm text-gray-500">
                            {request.travelers} voyageur{request.travelers > 1 ? 's' : ''}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {formatDate(request.departureDate)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {formatBudget(request.budget)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusColors[request.status]}`}>
                            {statusLabels[request.status]}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center gap-4">
                            {quote ? (
                              <>
                                <button
                                  onClick={() => setSelectedRequest(request.id)}
                                  className="text-blue-600 hover:text-blue-900"
                                >
                                  <Eye className="w-5 h-5" />
                                </button>
                                <button
                                  onClick={() => {/* TODO: Implement send quote */}}
                                  className="text-green-600 hover:text-green-900"
                                  title="Renvoyer le devis"
                                >
                                  <Send className="w-5 h-5" />
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => handleCreateQuote(request.id)}
                                className="text-blue-600 hover:text-blue-900"
                              >
                                Créer un devis
                              </button>
                            )}
                            <button
                              onClick={() => {/* TODO: Implement delete */}}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <QuoteFormModal
        isOpen={selectedRequest !== null}
        onClose={() => setSelectedRequest(null)}
        requestId={selectedRequest}
      />
    </AdminLayout>
  );
}